<template>
  <div class="row">
    <PublicHeader :title="`Plano de Governo`">
      <div class="col-xs-12 col-sm-6">
        <p>
          Um plano de governo é um documento elaborado por um(a) candidato(a) a
          um cargo do Poder Executivo — como presidente, governador ou prefeito
          — no qual são apresentadas suas propostas, metas e diretrizes para a
          gestão pública durante o mandato, caso seja eleito(a).
        </p>
        <p>
          No Brasil, candidatos(as) são obrigados a registrar um plano de
          governo ao se candidatar a cargos executivos. Esse plano é
          disponibilizado pela Justiça Eleitoral, geralmente no site do TSE
          (Tribunal Superior Eleitoral).
        </p>
      </div>
    </PublicHeader>
    <div class="container">
      <Breadcrumb :links="[{ label: `Plano de Governo - ${anoPlano}` }]" />

      <div class="row">
        <div class="col-xs-12">
          <h2>Resumo</h2>
        </div>

        <div class="col-xs-12 col-sm-5 col-md-4">
          <p class="text-lg">
            Nos anos de
            <select
              name="plano-governo"
              id="plano-governo"
              v-model="anoPlano"
              @change="loadCompromissosPorAno"
            >
              <option value="2025-2028">2025-2028</option>
              <option value="2021-2024">2021-2024</option>
            </select>
            , o Prefeito Municipal de Santos apresentou a Justiça Eleitoral um
            total de
            <ValorDestaque
              :value="compromissos.length"
              :isLoading="isLoading"
            />
            metas para o seu Plano de Goveno.
          </p>
          <p class="text-lg">
            Os compromissos estão distribuidos entre
            <ValorDestaque
              :value="
                `${getTotalCompromissosPorSecretaria.length} entidades / unidades`
              "
            />
            municipais, podendo-se destacar:
          </p>
          <ul>
            <li
              v-for="item in top5SecretariasCompromissos"
              :key="item.sigla"
            >
              <ValorDestaque :value="`${item.nome}:`" />{{ item.total }}
            </li>
          </ul>
        </div>

        <div class="col-xs-12 col-sm-7 col-md-8">
          <BarChart
            :chartData="chartData"
            :gradiente="false"
            :css-classes="'chartWrapper'"
            :options="chartOptions"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <h2>Status</h2>
        </div>

        <div v-if="resumoStatus.length > 0" class="col-xs-12 col-sm-5">
          <p class="text-lg">
            Destes compromissos,
            <span
              v-for="(item, index) in resumoStatus"
              :key="index"
            >
              <ValorDestaque :value="item.total" :isLoading="isLoading" />
              <span v-html="item.texto"></span>
              <span v-if="index < resumoStatus.length - 2">, </span>
              <span v-if="index === resumoStatus.length - 2"> e </span>
            </span>.
          </p>
        </div>

        <div v-if="resumoStatus.length > 0" class="col-xs-12 col-sm-7">
          <PieChart
            :chartData="statusChartData"
            :gradiente="false"
            :css-classes="'chartWrapper'"
            :options="statusChartOptions"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <h2>Secretarias</h2>
        </div>

        <div
          class="col-xs-12 col-sm-4 col-md-3"
          v-for="secretaria in getSecretariasComResumo"
          :key="`card-${secretaria.sigla}`"
        >
          <CardInstitucional
            :item="{
              ...secretaria,
              route: { name: 'planoporsecretaria', params: { secretaria: secretaria.sigla } }
            }"
            :value="'teste'"
          />
        </div>
      </div>
      
      <div class="row" style="margin-top: 40px;">
        <div class="col-xs-12">
          <h2>Compromissos</h2>
        </div>

        <div class="col-xs-12" style="margin-bottom: 20px;">
          <div class="filtro-wrapper">
            <input 
              type="text"
              v-model="termoPesquisa"
              class="form-control"
              placeholder="Pesquisar por descritivo ou status..."
              @keyup.enter="pesquisar"
            />
            <button class="btn btn-primary" @click="pesquisar">Pesquisar</button>
          </div>
        </div>

        <div class="col-xs-12">
          <div class="table-wrapper">
            <data-table-public
              ref="compromissoTable"
              :has-auth="false"
              title="Lista de Compromissos"
              api-url="planodegoverno/compromissos"
              :fields="model.columns"
              :append-params="filtrosDataTable"
              :show-empty="true"
            >
              <template slot="columncustom" slot-scope="props">
                <span :class="['status-tag', getStatusClass(props.rowData.status)]">{{ props.rowData.status }}</span>
              </template>

              <template slot="rowrender" slot-scope="props">
                <button
                  v-if="props.rowData.acoes && props.rowData.acoes.length > 0"
                  class="btn btn-primary btn-sm"
                  @click="abrirSidePanel(props.rowData.acoes)"
                >
                  Ver Ações
                </button>
                <span v-else></span>
              </template>
            </data-table-public>
          </div>
        </div>
      </div>
    </div>

    <div :class="['side-panel-overlay', { 'is-open': isSidePanelOpen }]" @click="fecharSidePanel">
      <div class="side-panel-content" @click.stop>
        <button class="side-panel-close" @click="fecharSidePanel">&times;</button>
        <h3>Ações do compromisso</h3>

        <div class="side-panel-body">
          <div v-if="sidePanelChartData" class="modal-chart-container">
            <PieChart 
              :chartData="sidePanelChartData"
              :options="sideChartOptions"
              :height="150"
            />
          </div>

          <div class="filtro-wrapper filtro-modal">
            <input 
              type="text"
              v-model="termoPesquisaAcoes"
              class="form-control"
              placeholder="Pesquisar por descritivo ou status..."
            />
          </div>
    
          <div v-if="acoesFiltradas && acoesFiltradas.length > 0" class="acoes-list">
            <div v-for="acao in acoesFiltradas" :key="acao.id" class="acao-item">
              <div class="acao-info">
                <p class="acao-descricao">{{ acao.descritivo }}</p>
                <p class="acao-unidade"><strong>Unidade:</strong> {{ acao.nome_unidade }}</p>
              </div>
              <div class="acao-status">
                <span :class="['status-tag', getStatusClass(acao.status)]">{{ acao.status }}</span>
              </div>
            </div>
          </div>

          <div v-else>
            <p>Nenhuma ação encontrada.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import {
  PublicHeader,
  ValorDestaque,
  BarChart,
  PieChart,
  Breadcrumb,
  CardInstitucional
} from '../../../components'
import colors from '@/utils/colors'
import DataTablePublic from '@/components/common/DataTablePublic';

export default {
  components: {
    PublicHeader,
    ValorDestaque,
    BarChart,
    PieChart,
    Breadcrumb,
    CardInstitucional,
    DataTablePublic
  },
  data: () => {
    return {
      anoPlano: '2025-2028',
      totalMetas: 0,
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false
      },
      statusChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom'
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const currentValue = dataset.data[tooltipItem.index];
              const percentage = ((currentValue / total) * 100).toFixed(1) + '%';
              const label = data.labels[tooltipItem.index] || '';
              return `${label}: ${currentValue} (${percentage})`;
            }
          }
        },
        plugins: {
          datalabels: {
            color: function(context) {
              const backgroundColor = context.dataset.backgroundColor[context.dataIndex];

              if (backgroundColor === '#222222' || backgroundColor === '#AD68A0' || backgroundColor === '#5C8A95') {
                return '#FFFFFF';
              }
            },
            font: {
              weight: 'bold'
            },
          }
        }
      },
      termoPesquisa: '',
      termoPesquisaAcoes: '',
      isSidePanelOpen: false,
      acoesSidePanel: [],
      sidePanelChartData: null,
      model: {
        columns: [
          {
            name: 'descritivo',
            title: 'Descritivo',
            dataClass: 'align-middle',
          },
          {
            name: '__slot:column',
            title: 'Status',
            titleClass: 'text-center',
            dataClass: 'text-center align-middle',
          },
          {
            name: '__slot:actions',
            title: 'Ações',
            titleClass: 'text-center',
            dataClass: 'text-center align-middle',
          },
        ]
      },
      sideChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'right'
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const currentValue = dataset.data[tooltipItem.index];
              const percentage = ((currentValue / total) * 100).toFixed(1) + '%';
              const label = data.labels[tooltipItem.index] || '';
              return `${label}: ${currentValue} (${percentage})`;
            }
          }
        },
        plugins: {
          datalabels: {
            color: function(context) {
              const backgroundColor = context.dataset.backgroundColor[context.dataIndex];

              if (backgroundColor === '#222222' || backgroundColor === '#AD68A0' || backgroundColor === '#5C8A95') {
                return '#FFFFFF';
              }
            },
            font: {
              weight: 'bold'
            },
          }
        }
      }
    }
  },
  created() {
    this.loadCompromissosPorAno()
  },
  methods: {
    loadCompromissosPorAno() {
      this.$store.dispatch('plano_governo/loadCompromissosAno', this.anoPlano)

      this.$nextTick(() => {
        if (this.$refs.compromissoTable) {
          this.$refs.compromissoTable.refresh();
        }
      });
    },
    pesquisar() {
      this.$refs.compromissoTable.refresh();
    },
    getStatusClass(status) {
        if (!status) return '';
        const statusSemAcentos = status.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        const statusNormalized = statusSemAcentos.toLowerCase().replace(/\s+/g, '-');
        return `status-${statusNormalized}`;
    },
    abrirSidePanel(acoes) {
      const counts = acoes.reduce((acc, acao) => {
        const status = acao.status || 'Não Informado';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      this.sidePanelChartData = {
        labels: Object.keys(counts),
        datasets: [{
          data: Object.values(counts),
          backgroundColor: colors.coresHexa,
          color: '#FFF'
        }]
      }

      this.acoesSidePanel = acoes;
      this.isSidePanelOpen = true;
    },
    fecharSidePanel() {
      this.isSidePanelOpen = false;
      setTimeout(() => {
        this.acoesSidePanel = [];
        this.sidePanelChartData = null;
        this.termoPesquisaAcoes = '';
      }, 300);
    }
  },
  computed: {
    ...mapState({
      compromissos: ({ plano_governo }) => plano_governo.compromissos,
      isLoading: ({ plano_governo }) => plano_governo.loading
    }),
    ...mapGetters('plano_governo', ['getSecretarias', 'getTotalCompromissosPorSecretaria', 'getTotalCompromissosPorStatus', 'getSecretariasComResumo']),
    top5SecretariasCompromissos() {
      return this.getTotalCompromissosPorSecretaria.slice(0, 5);
    },
    resumoStatus() {
      return this.getTotalCompromissosPorStatus
        .filter(item => item.total > 0)
        .map(item => {
          const singular = item.total === 1;
          let texto = '';

          switch (item.status){
            case 'Concluído':
              texto = singular ? 'está <strong>concluído</strong>' : 'estão <strong>concluídos</strong>';
              break;
            case 'Em andamento':
              texto = singular ? 'está <strong>em andamento</strong>' : 'estão <strong>em andamento</strong>';
              break;
            case 'Não Iniciado':
              texto = singular ? 'não foi <strong>iniciado</strong>' : 'não foram <strong>iniciados</strong>';
              break;
            case 'Cancelado':
              texto = singular ? 'foi <strong>cancelado</strong>' : 'foram <strong>cancelados</strong>';
              break;
          }
          return { total: item.total, texto: texto };
        });
    },
    chartData() {
      const secretarias = this.getTotalCompromissosPorSecretaria.map(item => item.sigla)
      const valores = this.getTotalCompromissosPorSecretaria.map(item => item.total)
      
      return {
        labels: secretarias,
        datasets: [
          {
            data: valores,
            label: 'Unidades',
            backgroundColor: colors.coresHexa,
            color: '#FFF'
          }
        ]
      }
    },
    statusChartData() {
      const label = this.getTotalCompromissosPorStatus.map(item => item.status);
      const valores = this.getTotalCompromissosPorStatus.map(item => item.total);

      return {
        labels: label,
        datasets: [
          {
            data: valores,
            label: 'Status',
            backgroundColor: colors.coresHexa,
            color: '#FFF'
          }
        ]
      }
    },
    filtrosDataTable() {
      const filtros = {
        periodo: this.anoPlano
      };

      if (this.termoPesquisa) {
        filtros.pesquisa = this.termoPesquisa;
      }

      return filtros;
    },
    acoesFiltradas() {
      if (!this.acoesSidePanel || this.acoesSidePanel.length === 0) {
        return [];
      }
      if (!this.termoPesquisaAcoes) {
        return this.acoesSidePanel;
      }
      const termo = this.termoPesquisaAcoes.toLowerCase();
      return this.acoesSidePanel.filter(acao => {
        const descritivo = acao.descritivo ? acao.descritivo.toLowerCase() : '';
        const status = acao.status ? acao.status.toLowerCase() : '';
        const nomeUnidade = acao.nome_unidade ? acao.nome_unidade.toLowerCase() : '';
        return descritivo.includes(termo) || status.includes(termo) || nomeUnidade.includes(termo);
      });
    }
  }
}
</script>

<style>
#plano-governo {
  background-color: transparent;
  border: 1px solid #468b35;
  border-radius: 5px;
  padding: 5px 10px;
  margin: 0 5px;
}
#plano-governo:hover {
  border: 1px solid #a1be4d;
  cursor: pointer;
}

option {
  color: #000;
}

.chartWrapper {
  max-height: 350px;
}

ul {
  padding-left: 20px;
}

.table-wrapper {
  overflow-x: auto;
}

.filtro-wrapper {
  display: flex;
  gap: 10px;
}
.filtro-modal {
  margin-bottom: 20px;
}

.acao-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px 0;
  border-bottom: 1px solid #e9ecef;
}
.acao-item:last-child {
  border-bottom: none;
}
.acao-info {
  flex-grow: 1;
  margin-right: 20px;
}
.acao-descricao {
  margin: 0 0 10px 0;
  font-size: 1.05em;
  color: #333;
}
.acao-unidade {
  font-size: 0.9em;
  color: #555;
  font-style: italic;
}
.acao-unidade strong {
  font-style: normal;
}
.acao-status {
  flex-shrink: 0;
}

.status-tag {
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.85em;
  font-weight: bold;
  color: #fff;
  text-transform: capitalize;
  white-space: nowrap;
  display: inline-block
}

.status-nao-iniciado {
  background-color: #6c757d;
}

.status-em-andamento {
  background-color: #007bff;
}

.status-concluida, .status-concluido {
  background-color: #28a745;
}

.status-cancelada, .status-cancelado, 
.status-rejeitada {
  background-color: #dc3545;
}

.side-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 2000;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.4s ease-in-out, visibility 0.4s;
}

.side-panel-overlay.is-open {
  visibility: visible;
  opacity: 1;
}

.side-panel-content {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 500px;
  max-width: 90%;
  background-color: #f8f9fa;
  box-shadow: -2px 0 15px rgba(0,0,0,0.2);
  padding: 20px;
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.4s ease;
}

.side-panel-overlay.is-open .side-panel-content {
  transform: translateX(0);
}

.side-panel-close {
    position: absolute;
    top: 10px;
    right: 15px;
    border: none;
    background: transparent;
    font-size: 2.5em;
    font-weight: 300;
    color: #888;
    cursor: pointer;
    line-height: 1;
}
.side-panel-close:hover {
  color: #333;
}

.side-panel-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  padding-right: 30px;
}

.side-panel-body {
  flex-grow: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-chart-container {
  width: 100%;
  margin-bottom: 20px;
}

.acoes-list {
  flex-grow: 1;
}
</style>



// WEBPACK FOOTER //
// src/views/public/plano_governo/index.vue